import { Popover, PopoverContent, PopoverTrigger } from "@ondit/ui";
import { CircleQuestionMark } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useT } from "~/core/i18n";

/** Keep the data scope beside page actions so every reading has the same header. */
export function PageHead({
	title,
	icon,
	within,
	lede,
	actions,
	filters,
}: {
	title: string;
	icon: ReactNode;
	/** Optional parent section, subordinate to the current page title. */
	within?: string;
	lede?: string;
	actions?: ReactNode;
	filters?: ReactNode;
}) {
	return (
		<div className="page-top">
			<div className="head">
				<div className="head-text">
					<div className="title-row">
						{icon ? (
							<span className="page-icon" aria-hidden>
								{icon}
							</span>
						) : null}
						<h1>{title}</h1>
						{within ? (
							<>
								<span className="title-sep" aria-hidden>
									/
								</span>
								<span className="title-within">{within}</span>
							</>
						) : null}
						{lede ? <Hint text={lede} /> : null}
					</div>
				</div>
				{filters || actions ? (
					<div className="head-tools">
						{filters ? <div className="page-scope">{filters}</div> : null}
						{actions ? <div className="head-actions">{actions}</div> : null}
					</div>
				) : null}
			</div>
		</div>
	);
}

/** A titled block: what this is, and one line saying what the number means. */
export function Section({
	title,
	icon,
	lede,
	action,
	children,
}: {
	title: string;
	icon?: ReactNode;
	lede?: string;
	action?: ReactNode;
	children: ReactNode;
}) {
	return (
		<section className="section">
			<div className="section-head">
				<div className="title-row">
					{icon}
					<h2>{title}</h2>
					{lede ? <Hint text={lede} /> : null}
				</div>
				{action ? <div className="spacer">{action}</div> : null}
			</div>
			{children}
		</section>
	);
}

/**
 * One figure and its label.
 *
 * The caller decides what "nothing measured" looks like — an em dash everywhere
 * in this product and never a zero, because a zero is a measurement and "we
 * have not looked yet" is not one. It also decides whether the figure opens
 * onto the rows it was counted from: that drawer is the Analytics module's, and
 * a card this generic cannot import one module's component without the shell
 * depending on a module.
 */
export function Stat({
	label,
	value,
	foot,
	hint,
	action,
	mono = true,
}: {
	label: string;
	/** A node rather than a string so a module can wrap its own figure — a
	 *  drill-down into the rows behind it, say. */
	value: ReactNode;
	/** A second line under the figure. A node, because it is often a second
	 *  figure with a drill-down of its own. */
	foot?: ReactNode;
	/** How this number is arrived at, in one sentence. */
	hint?: string;
	/** What to do about this figure, on the card the figure is on. It sits in
	 *  the card's top corner, out of the flow, so that a card with an action
	 *  and a card without one still put their figures on the same line. */
	action?: ReactNode;
	/** Off when the value is words rather than a figure — "an hour ago". */
	mono?: boolean;
}) {
	return (
		<div className={action ? "stat-card has-action" : "stat-card"}>
			<span className="label">
				{label}
				{hint ? <Hint text={hint} /> : null}
			</span>
			<span className={mono ? "value num" : "value"}>{value}</span>
			{foot ? <span className="foot">{foot}</span> : null}
			{action ? <span className="stat-action">{action}</span> : null}
		</div>
	);
}

/**
 * What a heading or a figure means, opened by click or keyboard.
 *
 * Every number in this panel is one somebody may quote in a meeting, and a
 * number whose definition lives in the documentation gets quoted as something
 * else. The sentence is the definition, not a tip — which is why it is a mark
 * beside the thing it defines and not a paragraph under it: worth reading once,
 * and in the way on every visit after that.
 */
export function Hint({ text }: { text: string }) {
	const t = useT();
	const [open, setOpen] = useState(false);
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				{/* A span keeps explanations available inside read-only disabled fieldsets. */}
				{/* biome-ignore lint/a11y/useSemanticElements: native buttons cannot be opened inside a disabled fieldset */}
				<span
					className="hint-mark"
					role="button"
					tabIndex={0}
					aria-label={t("hint.label")}
					onClick={(event) => {
						// Label activation otherwise focuses its input and immediately dismisses the popover.
						event.preventDefault();
						setOpen((value) => !value);
					}}
					onKeyDown={(event) => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							setOpen((value) => !value);
						}
					}}
				>
					<CircleQuestionMark aria-hidden />
				</span>
			</PopoverTrigger>
			<PopoverContent className="whitespace-pre-line text-sm" maxWidth={320} sideOffset={6}>
				{text}
			</PopoverContent>
		</Popover>
	);
}
